import { inject, Injectable } from "@angular/core";
import { AppData } from "../data";
import { ModalController } from "@ionic/angular";
import { RouteStatusUpdateResult } from "../types";
import { TranslateService } from "./translate.service";
import { NotificationService } from "./notification.service";
import { Title } from "@angular/platform-browser";
import { Router } from "@angular/router";
import { LocationService } from "./location.service";
import { SessionService } from "./session.service";

@Injectable({ providedIn: 'root' })

export class LogoutService {

  private _data = inject(AppData);
  private _locationService = inject(LocationService);
  private _modalController = inject(ModalController);
  private _router = inject(Router);
  private _sessionService = inject(SessionService);
  private _title = inject(Title);
  private _translateService = inject(TranslateService);
  private _notificationService = inject(NotificationService);

  public async handleWasLoggedOut(response: RouteStatusUpdateResult) {
    console.debug('logout callback');
    await this.closeModals();

    // stop GPS reporting immediately
    this._data.set('driverIsActivelyReporting', false);

    // user notifications
    const message = this.getNotificationMessage(response);
    if (message) { this._notificationService.warning(message, null, true); }
    const title = this._translateService.translate('ACTION.login');

    //  clear and reset data
    await this._locationService.stop();
    this._sessionService.clearSession();

    //  return user to login
    if (this._router.url === '/login') { return; }
    this._title.setTitle(title);
    this._router.navigateByUrl('/login');
  }

  private async closeModals(): Promise<boolean> {
    return this._modalController?.dismiss()
      .catch(err => {
        //  if no dialog was open will throw error
        console.warn(err);
        return false;
      });
  }

  private getNotificationMessage(logoutInfo: RouteStatusUpdateResult): string {
    let message = this._translateService.translate('MESSAGE.logout.fallback');
    const { driverFirstName, driverLastName, routeNumber, vehicleNumber, desync } = logoutInfo || { desync: false };
    if (
      !this._data.route?.id
      || (
        driverFirstName === this._data.route?.driverFirstName
        && driverLastName === this._data.route?.driverLastName
        && vehicleNumber === this._data.route?.vehicleNumber
        && routeNumber === this._data.route?.routeNumber
      )
    ) { //  current user logged themselves out, no need to notify
      message = null;
    } else if (logoutInfo?.messages?.length) {
      message = logoutInfo.messages.reduce((acc, message) => `${acc}${message.message} `, '');
    } else if (desync) {
      message = this._translateService.translate('MESSAGE.logout.desyncWarning');
    } else if ( //  logged out by someone else
      driverFirstName || driverLastName || vehicleNumber || routeNumber
    ) {
      message = this._translateService.translate('MESSAGE.logout.by');
      if (driverFirstName) { message += ` ${driverFirstName}`; }
      if (driverLastName) { message += ` ${driverLastName}`; }
      if (routeNumber) { message += ` ${this._translateService.translate('MESSAGE.logout.route')} ${routeNumber}`; }
      if (vehicleNumber) { message += ` ${this._translateService.translate('MESSAGE.logout.vehicle')} ${vehicleNumber}`; }
    }
    return message;
  }

}
